import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import isNumber from 'lodash/isNumber';
import { RadioGroup, Radio } from 'react-magma-dom';

import Button from 'components/Button';
import Input from '../Input';

import { getBinWidth, getMinBinWidth, getStartingPoint, getMinStartingPoint } from 'store/scenes/graphicalStatistics/rightPane/selectors';
import { setBinWidth, setStartingPoint, setMinStartingPoint, setMinBinWidth } from 'store/scenes/graphicalStatistics/rightPane/actions';
import { getDomainValueByName } from 'store/dataset';

import { makeSettingsHistogramValidator, maxNumberOfBinsForHistogram } from 'utilities/validators';

export const FREQUENCY = 'Frequency';
export const RELATIVE_FREQUENCY = 'Relative Frequency';

const AdditionalFields = ({
  onRecalculateGraph,
  onFrequencyRadioButton,
  showHistogramFields,
  showRadioFrequency,
  typeOfFrequency,
  parameter
}) => {
  const dispatch = useDispatch();

  const histogramSettings = {
    binWidth: useSelector(state => getBinWidth(state)),
    startingPoint: useSelector(state => getStartingPoint(state)),
    minBinWidth: useSelector(state => getMinBinWidth(state)),
    minStartPoint: useSelector(state => getMinStartingPoint(state)),
    domainValue: useSelector(state => getDomainValueByName(state, parameter))
  };

  const maxStartPoint = histogramSettings.domainValue.min;
  const maxValue = histogramSettings.domainValue.max;

  const [width, setWidth] = useState(histogramSettings.binWidth);
  const [stPoint, setStPoint] = useState(histogramSettings.startingPoint);
  const [minWidth, setMinWidth] = useState(histogramSettings.minBinWidth);
  const [minStPoint, setMinStPoint] = useState(histogramSettings.minStartPoint);

  const setHistogramSetting = () => {
    dispatch(setBinWidth(width));
    dispatch(setStartingPoint(stPoint));
    dispatch(setMinBinWidth(minWidth));
    dispatch(setMinStartingPoint(minStPoint));
  };

  const isErrorBinWidth = isNaN(width) || !isNumber(width) || minWidth > width;
  const isErrorStartingPoint =
    isNaN(stPoint) || !isNumber(stPoint) || stPoint < minStPoint || stPoint > maxStartPoint;

  const isButtonDisabled = showHistogramFields
    ? isErrorBinWidth || isErrorStartingPoint
    : false;

  const handleBinWidthChange = ({ target: { value } }) => {
    if (+value) {
      let minStartPoint = maxValue - (maxNumberOfBinsForHistogram * +value);
      if (minStartPoint > maxStartPoint) {
        minStartPoint = maxStartPoint;
      }
      const minBinWidth = (maxValue - minStartPoint) / maxNumberOfBinsForHistogram;
      setMinWidth(minBinWidth);
      setMinStPoint(minStartPoint);
    }
    setWidth(value ? +value : '');
  };

  const handleStartPointChange = ({ target: { value } }) => {
    let minStartPoint = maxValue - (maxNumberOfBinsForHistogram * width);
    if (minStartPoint > maxStartPoint) {
      minStartPoint = maxStartPoint;
    }
    const minBinWidth = (maxValue - minStPoint) / maxNumberOfBinsForHistogram;

    setMinWidth(minBinWidth);
    setMinStPoint(minStartPoint);

    setStPoint(value ? +value : '');
  };

  const handleRecalculateGraph = () => {
    if (showHistogramFields) {
      setHistogramSetting();
    }
    if (showRadioFrequency) {
      onRecalculateGraph();
    }
  };

  const validateBinWidth = makeSettingsHistogramValidator(minWidth)(width);
  const errorStartingPoint = stPoint > maxStartPoint
    ? `Starting Point must be less than minimum ${parameter} value.`
    : makeSettingsHistogramValidator(maxStartPoint)(stPoint);

  return (
    <div>
      {showHistogramFields &&
        <>
          <p className="font-700 mb-5">
            Histogram Settings
          </p>
          <div className="flex space-between baseline">
            <Input
              type="number"
              label="Bin/Class Width"
              name="binWidth"
              min={minWidth}
              step={0.0001}
              value={width}
              onChange={handleBinWidthChange}
              invalid={isErrorBinWidth}
              error={validateBinWidth}
              wrapperClassName="mb-15 relative width-48"
            />

            <Input
              type="number"
              label="Starting Point"
              name="startingPoint"
              min={minStPoint}
              max={maxStartPoint}
              step={0.0001}
              value={stPoint}
              onChange={handleStartPointChange}
              invalid={isErrorStartingPoint}
              error={errorStartingPoint}
              wrapperClassName="mb-15 relative width-48"
            />
          </div>
        </>
      }
      {showRadioFrequency &&
        <RadioGroup
          labelText="Category Labels"
          name="frequency-radio"
          className="frequency-radio_group"
          onChange={onFrequencyRadioButton}
          value={typeOfFrequency}
        >
          <Radio
            labelText={FREQUENCY}
            value={FREQUENCY}
            labelStyle={{ paddingLeft: 0 }}
          />
          <Radio
            labelText={RELATIVE_FREQUENCY}
            value={RELATIVE_FREQUENCY}
            labelStyle={{ paddingLeft: 0 }}
          />
        </RadioGroup>
      }
      <div>
        <Button
          className="main-button--green mt-10"
          data-testid="btn-recalculate-graphs"
          disabled={isButtonDisabled}
          onClick={handleRecalculateGraph}
        >
          Recalculate Graph
        </Button>
      </div>
    </div>
  );
};

AdditionalFields.propTypes = {
  showRadioFrequency: PropTypes.bool.isRequired,
  showHistogramFields: PropTypes.bool.isRequired,
  typeOfFrequency: PropTypes.string.isRequired,
  onFrequencyRadioButton: PropTypes.func.isRequired,
  onRecalculateGraph: PropTypes.func.isRequired,
  parameter: PropTypes.string.isRequired
};

export default AdditionalFields;
